import { useState, useEffect } from "react";
import api from "../utils/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import CreateServiceWizard from "./CreateServiceWizard";

const ServicesTabImproved = () => {
  const [services, setServices] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [viewMode, setViewMode] = useState("card"); // card or list

  useEffect(() => {
    loadServices();
    loadAssets();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/services');
      setServices(response.data || []);
    } catch (error) {
      console.error("Error loading services:", error);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      setAssetsLoading(true);
      const response = await api.get('/assets');
      setAssets(response.data || []);
    } catch (error) {
      console.error("Error loading assets:", error);
    } finally {
      setAssetsLoading(false);
    }
  };

  const openServiceModal = (service = null) => {
    setEditingService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;

    try {
      await api.delete(`/services/${serviceId}`);
      toast.success("Service deleted successfully");
      loadServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Failed to delete service");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading services...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-0">
      {/* Header - No Card, just content */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Services</h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Manage bookable services
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === "card" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("card")}
            className="flex-1 sm:flex-none text-xs sm:text-sm"
          >
            Cards
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("list")}
            className="flex-1 sm:flex-none text-xs sm:text-sm"
          >
            List
          </Button>
          <Button onClick={() => openServiceModal()} className="text-xs sm:text-sm" disabled={assetsLoading || loading}>
            {assetsLoading ? 'Loading...' : '+ Add'}
          </Button>
        </div>
      </div>

      {/* Services */}
      {services.length === 0 ? (
        <Card>
          <CardContent className="py-12 sm:py-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center text-3xl sm:text-4xl">📦</div>
            <p className="text-gray-600 font-medium mb-2 text-sm sm:text-base">No services yet</p>
            <p className="text-xs sm:text-sm text-gray-500 mb-6">
              Create your first service
            </p>
            <Button onClick={() => openServiceModal()} className="text-sm">
              + Add Service
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === "card" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {services.map((service) => (
                <Card key={service.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-40 sm:h-48 bg-gray-100">
                    {service.images && service.images.length > 0 ? (
                      <img 
                        src={service.images[service.mainImageIndex || 0]} 
                        alt={service.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-4xl sm:text-5xl">🖼️</div>
                    )}
                    {service.active ? (
                      <span className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-green-500 text-white">
                        Active
                      </span>
                    ) : (
                      <span className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-gray-400 text-white">
                        Inactive
                      </span>
                    )}
                  </div>
                  <CardContent className="p-3 sm:p-4">
                    <h3 className="font-semibold text-base sm:text-lg mb-1">{service.name}</h3>
                    {service.tagline && (
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">{service.tagline}</p>
                    )}
                    {service.description && (
                      <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 mb-3">
                        {service.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                      <span className="font-semibold text-blue-600">
                        ${service.price || 0}
                      </span>
                      {service.duration && <span>{service.duration}min</span>}
                      {service.capacity && <span>{service.capacity} max</span>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openServiceModal(service)}
                        className="flex-1 text-xs sm:text-sm"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteService(service.id)}
                        className="text-red-600 hover:text-red-700 text-xs sm:text-sm"
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Service
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                          Price
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                          Duration
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">
                          Status
                        </th>
                        <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {services.map((service) => (
                        <tr key={service.id} className="hover:bg-gray-50">
                          <td className="px-4 sm:px-6 py-3 sm:py-4">
                            <div className="flex items-center gap-2 sm:gap-3">
                              {service.images && service.images.length > 0 ? (
                                <img 
                                  src={service.images[service.mainImageIndex || 0]} 
                                  alt={service.name} 
                                  className="w-10 h-10 sm:w-12 sm:h-12 rounded object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-gray-100 flex items-center justify-center text-xl">📦</div>
                              )}
                              <div>
                                <div className="font-semibold text-xs sm:text-sm">{service.name}</div>
                                {service.tagline && (
                                  <div className="text-xs text-gray-500 hidden sm:block">{service.tagline}</div>
                                )}
                                <div className="text-xs text-gray-600 sm:hidden">
                                  ${service.price} • {service.duration}min
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-xs sm:text-sm font-semibold text-blue-600 hidden sm:table-cell">
                            ${service.price || 0}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-xs sm:text-sm text-gray-600 hidden md:table-cell">
                            {service.duration ? `${service.duration} min` : '-'}
                          </td>
                          <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                            {service.active ? (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-1 sm:gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openServiceModal(service)}
                                className="text-xs"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteService(service.id)}
                                className="text-red-600 hover:text-red-700 text-xs"
                              >
                                Del
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent className="max-w-xl max-h-[95vh] p-6">
          <CreateServiceWizard
            editingService={editingService}
            onComplete={(savedService) => {
              loadServices();
              setShowServiceModal(false);
              setEditingService(null);
            }}
            onCancel={() => {
              setShowServiceModal(false);
              setEditingService(null);
            }}
            assets={assets}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesTabImproved;